import { slotSpan, timeToSlot } from '@/lib/utils'
import type { ClientReservation } from '@/types/api'

export interface PlacedLesson {
  lesson: ClientReservation
  /** 1-based start slot within the day grid. */
  slot: number
  /** How many slots the lesson covers (>= 1). */
  span: number
}

/**
 * Lessons that overlap in time cannot share a row, so each instructor block is
 * rendered as several sub-rows ("lanes"). This is the classic greedy
 * interval-partitioning: walk the lessons in start order and drop each one into
 * the first lane whose last lesson has already finished.
 *
 * The number of lanes equals the maximum number of simultaneous lessons, which
 * is the minimum possible - no lane is wasted.
 */
export function assignLanes(
  lessons: ClientReservation[],
  startHour: number,
  interval: number,
  totalParts: number,
): PlacedLesson[][] {
  const placed: PlacedLesson[] = lessons
    .map((lesson) => {
      const slot = timeToSlot(lesson.start, startHour, interval)
      if (slot === null) return null
      return {
        lesson,
        // Clamp into the visible grid: legacy data contains lessons that start
        // before opening or run past closing time.
        slot: Math.min(Math.max(slot, 1), totalParts),
        span: slotSpan(lesson.start, lesson.end, interval),
      }
    })
    .filter((x): x is PlacedLesson => x !== null)
    .map((p) => ({ ...p, span: Math.min(p.span, totalParts - p.slot + 1) }))
    .sort((a, b) => a.slot - b.slot || a.span - b.span)

  const lanes: PlacedLesson[][] = []
  const laneEnd: number[] = [] // exclusive end slot of each lane's last lesson

  for (const item of placed) {
    let target = laneEnd.findIndex((end) => end <= item.slot)
    if (target === -1) {
      target = lanes.length
      lanes.push([])
      laneEnd.push(0)
    }
    lanes[target].push(item)
    laneEnd[target] = item.slot + item.span
  }

  return lanes
}

/**
 * Turns one lane into a left-to-right list of grid cells: lessons plus the
 * empty gaps between them. Rendering gaps explicitly keeps the CSS grid
 * columns aligned without absolute positioning.
 */
export type LaneCell =
  | { kind: 'lesson'; slot: number; span: number; lesson: ClientReservation }
  | { kind: 'empty'; slot: number; span: 1 }

export function laneCells(lane: PlacedLesson[], totalParts: number): LaneCell[] {
  const cells: LaneCell[] = []
  let cursor = 1

  for (const { lesson, slot, span } of lane) {
    // Overlapping duplicates inside one lane should be impossible, but never
    // emit a negative gap if the data is odd.
    while (cursor < slot) {
      cells.push({ kind: 'empty', slot: cursor, span: 1 })
      cursor += 1
    }
    if (cursor > slot) continue
    cells.push({ kind: 'lesson', slot, span, lesson })
    cursor = slot + span
  }

  while (cursor <= totalParts) {
    cells.push({ kind: 'empty', slot: cursor, span: 1 })
    cursor += 1
  }

  return cells
}

/** An entirely empty lane - always appended so a new overlapping lesson can be
 *  created on any slot, even one that is already busy. */
export function emptyLane(totalParts: number): LaneCell[] {
  return Array.from({ length: totalParts }, (_, i) => ({
    kind: 'empty' as const,
    slot: i + 1,
    span: 1 as const,
  }))
}
